import React, { useEffect, useState } from 'react';
import { X, Megaphone, Loader2, CheckCircle, AlertCircle, Save } from 'lucide-react';
import axios from 'axios';
import { api_url_v1 } from '../utils/config';

const AnnouncementModal = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Get token
  const getToken = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('adminToken');
    }
    return null;
  };

  // Fetch existing announcement
//   const fetchAnnouncement = async () => {
//     try {
//       const token = getToken();
//       const res = await axios.get(`${api_url_v1}/getAnnouncement`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       if (res.data?.data?.body) {
//         setMessage(res.data.data.body);
//       }
//     } catch (err) {
//       console.error('Failed to fetch announcement');
//     }
//   };

//   useEffect(() => {
//     if (isOpen) {
//       fetchAnnouncement();
//     }
//   }, [isOpen]);

  // Save / update announcement
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setStatus('error');
      setErrorMessage('Announcement message cannot be empty');
      return;
    }

    setLoading(true);
    setStatus(null);
    setErrorMessage('');

    try {
      const token = getToken();
      await axios.post(
        `${api_url_v1}/editAnnouncement`,
        { body: message },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setStatus('success');

      setTimeout(() => {
        setStatus(null);
        onClose();
      }, 1500);
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err.response?.data?.message || 'Failed to save announcement'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setStatus(null);
      setErrorMessage('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Megaphone className="text-indigo-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Site Announcement
              </h2>
              <p className="text-sm text-gray-500">
                Display a message to all users on the site
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {status === 'success' && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="text-green-600" size={20} />
              <p className="text-green-800 text-sm font-medium">
                Announcement saved successfully
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="text-red-600" size={20} />
              <p className="text-red-800 text-sm font-medium">
                {errorMessage}
              </p>
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Announcement Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              disabled={loading}
              placeholder="Write announcement to be shown on the site..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none disabled:bg-gray-50"
            />
          </div>

          {/* Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <span className="font-medium">Heads up:</span> This announcement
              will be visible to all users across the site.
            </p>
          </div>

          {/* Footer */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Announcement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnnouncementModal;
